import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import CreateEditModal from 'common/components/modules/modal-wrapper';
import classNames from "classnames";
import PrivateMessageToMember from "./modules/private-message-to-member";
import QueryParams from "admin/utils/QueryParams";
import SwitchMembershipTagMembers from "./modules/tag-members";
import SwitchMembershipMembersList from "./modules/members-list";
import SwitchingFromToMemberships from "./modules/switching-from-to";
import SwitchMembershipDuration from "./modules/duration";
import SwitchMembershipConfirmModals from "./modules/confirm-modals";
import SwitchMembershipNotes from "./modules/notes/indexV2";
import { isEmpty } from "lodash";


const SwitchMembershipModal = ({
   isMobile,
   membersData = [],
   onCloseModal,
   isFetchingSwitchMembership,
   confirmModal,
   openCloseConfirmModal,
   confirmModalTypesOpenOnContainer,
   checkIfCanSwitchMembership,
   authUser,
   checkIsSubscribedToNewMembershipAndSwitch,
   switchToNewMembership,
   updateSwitchMembershipMembersData,
   isDemoMode,
}) => {
   const [payload, setPayload] = useState({
      selectedMembershipToSwitch: null,
      private_note: '<p>I switched their membership from &nbsp;<span class="editor-variable">&lt;current-membership&gt;</span>&nbsp; to &nbsp;<span class="editor-variable">&lt;new-membership&gt;</span></p>',
      private_message_to_member: '',
      tags_to_add: [],
      duration: {
         enabled: false,
         value_in_minutes: 60,
         show_countdown_to_member: true,
         message_to_member_when_duration_ends: '',
         private_note_when_duration_ends: '<p>Membership was automatically switched back to &nbsp;<span class="editor-variable">&lt;new-membership&gt;</span>&nbsp; from &nbsp;<span class="editor-variable">&lt;current-membership&gt;</span></p>',
         tags_to_remove_when_duration_ends: [],
         disableDuration: false,
      },
   })
   const [errorsData, setErrorsData] = useState({})
   const [isFetchingCheckCanSwitch, setIsFetchingCheckCanSwitch] = useState(false)
   const [expiredAndIncompletedMembers, setExpiredAndIncompletedMembers] = useState({ expred: [], incomplete: [] })

   useEffect(() => {
      QueryParams.addQueryParam('modal', 'switch-membership')

      return () => {
         QueryParams.removeQueryParam('modal')
      }
   }, [])

   useEffect(() => {
      if(membersData.length !== 0) {
         const expiredOrIncomletedMember = membersData.find(it => ['incomplete', 'expired'].includes(it.member_status))

         if(expiredOrIncomletedMember) {
            setPayload({
               ...payload,
               private_note: '<p>I switched their membership to &nbsp;<span class="editor-variable">&lt;new-membership&gt;</span></p>',
               duration: {
                  ...payload.duration,
                  enabled: true,
                  disableDuration: true,
                  private_note_when_duration_ends: '<p>Membership was automatically switched back from &nbsp;<span class="editor-variable">&lt;current-membership&gt;</span></p>',
               },
            })
         }
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if(membersData.length !== 0 && membersData.find(it => ['incomplete', 'expired'].includes(it.member_status))) {
         setExpiredAndIncompletedMembers({
            expred: membersData.filter(it => ['expired'].includes(it.member_status)),
            incomplete: membersData.filter(it => ['incomplete'].includes(it.member_status)),
         })
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [membersData])

   const switchingFromPricingsData = useMemo(() => {
      let result = []

      membersData.forEach(item => {
         if(!result.some(el => el.pricing_id === item.pricing_id)) {
            result.push({
               pricing_name: item.pricing_name,
               pricing_id: item.pricing_id,
            })
         }
      })

      return result

   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [membersData])

   const updatePayload = (data = {}) => {
      setPayload({
         ...payload,
         ...data,
      })
   }

   const handleSelectMembership = (value) => {
      updatePayload({
         selectedMembershipToSwitch: value,
      })
   }

   const onSelectTags = (selectedMemberTagIds = []) => {
      updatePayload({
         tags_to_add: selectedMemberTagIds,
      })
   }

   const buildPayload = () => {
      let result = {
         ...payload,
      }

      // Check for selected membership
      if(!result.selectedMembershipToSwitch) {
         return;
      }

      // Add new_membership_id and delete selectedMembershipToSwitch
      result.new_membership_id = result.selectedMembershipToSwitch.id
      delete result.selectedMembershipToSwitch

      // construct members id's
      result.members = membersData.map(item => item.id)

      // Add super admin session on payload
      result.superAdminSession = Boolean(localStorage.getItem('superAdminSession'))

      return result
   }

   const validatePayload = () => {
      let errorsResult = {};

      if(Boolean(payload.private_message_to_member) && payload.private_message_to_member.length < 25) {
         errorsResult.private_message_to_member = ['Minimum 25 character limit']
      }

      if(payload.duration.enabled && Boolean(payload.duration.message_to_member_when_duration_ends) && payload.duration.message_to_member_when_duration_ends.length < 25) {
         errorsResult['duration.message_to_member_when_duration_ends'] = ['Minimum 25 character limit']
      }

      return errorsResult;
   }

   const onClickSwitchMemberships = () => {
      const validationErrors = validatePayload()

      if(validationErrors && !isEmpty(validationErrors)) {
         setErrorState(validationErrors)

         return;
      }
      setIsFetchingCheckCanSwitch(true)
      checkIfCanSwitchMembership(
         () => {
            setIsFetchingCheckCanSwitch(false)
            openCloseConfirmModal('confirm_switch_memberships')
         },
         () => {
            setIsFetchingCheckCanSwitch(false)
         }
      )
   }

   const handleConfirmModalAction = () => {
      switch (confirmModal.type) {
         case 'confirm_switch_memberships':
            const data = buildPayload()
            checkIsSubscribedToNewMembershipAndSwitch(data)
            break;

         case 'members_already_subscribed':
            const switchPayload = buildPayload()
            switchToNewMembership(switchPayload)
            break;

         case 'switch_complete':
         case 'switch_in_progress':
            onCloseModal()
            openCloseConfirmModal(null)
            break;
         default:
            break;
      }
   }

   const onRemoveMemberFromList = (memberId) => {
      const updatedMembersData = membersData.filter(member => member.id !== memberId)
      updateSwitchMembershipMembersData(updatedMembersData)
   }

   const setErrorState = (data = {}) => {
      setErrorsData({
         ...errorsData,
         ...data,
      })
   }

   const MODAL_CONTENT_WRAPPER_ID = 'switch_membership_modal_content_wrapper'

   return (
      <>
         <CreateEditModal
            type='confirm'
            onCloseModal={ onCloseModal }
            isMobile={ isMobile }
            contentWidth='655px'
            icon='switch-membership'
            title='Switch membership'
            description={
               expiredAndIncompletedMembers.expred?.length || expiredAndIncompletedMembers.incomplete?.length ?
                  'Members will gain access to their new membership benefits'
                  : 'Pricing terms remain the same, but members will gain access to their new membership benefits'
            }
            buttonText='More info on switching memberships'
            buttonIconName='help'
            primaryButtonClassName='gap-2 !px-[14px]'
            confirmButtonProperties={ {
               iconPosition: 'left',
               textMarginX: '0',
               fontIconSize: 'extraLarge',
            } }
            action={ () => {
               console.log('TODO_SWITCH_MEMBERSHIP open article');
            } }
            secondButtonTitle='Switch memberships'
            secondButtonWrapperClassName='w-full'
            secondButtonDisabled={ !Boolean(payload.selectedMembershipToSwitch) || isDemoMode }
            overrideSecondButtonProps={ {
               primaryColor: true,
               textColor: 'major-dark',
               backgroundColor: null,
               dataToolTipContent: isDemoMode ? 'Not available in demo' : null,
               isLoading: isFetchingCheckCanSwitch,
            } }
            secondButtonClassName={ classNames({
               '!cursor-default hover:!bg-action': isFetchingCheckCanSwitch,
            }) }
            secondaryButtonClassName='!px-[14px]'
            cancelButtonProperties={ {
               textMarginX: '0',
            } }
            modalWrapperClassName='!max-h-[90%]'
            isPreventDeafault={ false }
            secondButtonAction={ onClickSwitchMemberships }
            isScrollableTopPartWithoutButtons
            scrollableContentWrapperId={ MODAL_CONTENT_WRAPPER_ID }
            scrollableContentWrapperClassName='pr-2'

         >
            <div
               className='w-full mt-7 flex flex-col items-center gap-4 text-start'
            >
               {/* Member or members */}
               {
                  (expiredAndIncompletedMembers.expred?.length || expiredAndIncompletedMembers.incomplete?.length) ?
                     <>
                        {
                           !!expiredAndIncompletedMembers.expred?.length &&
                           <SwitchMembershipMembersList
                              membersData={ expiredAndIncompletedMembers?.expred || [] }
                              onRemoveMemberFromList={ onRemoveMemberFromList }
                              memberType='expired'
                           />
                        }
                        {
                           !!expiredAndIncompletedMembers.incomplete?.length &&
                           <SwitchMembershipMembersList
                              membersData={ expiredAndIncompletedMembers?.incomplete || [] }
                              onRemoveMemberFromList={ onRemoveMemberFromList }
                              memberType='incomplete'
                           />
                        }
                     </>
                     :
                     <SwitchMembershipMembersList
                        membersData={ membersData }
                        onRemoveMemberFromList={ onRemoveMemberFromList }
                     />
               }
               {/* switching from - to */}
               <SwitchingFromToMemberships
                  isMobile={ isMobile }
                  switchingFromPricingsData={ switchingFromPricingsData }
                  selectedMembershipToSwitch={ payload.selectedMembershipToSwitch }
                  onSelectMembership={ handleSelectMembership }
                  expiredOrIncomletedMembers={ expiredAndIncompletedMembers.expred?.length || expiredAndIncompletedMembers.incomplete?.length }
               />

               {/* Tag members */}
               <SwitchMembershipTagMembers
                  selectedMemberTagIds={ payload.tags_to_add }
                  onSelectTags={ onSelectTags }
                  title='Tag members'
               />
               {/* Private message */}
               <PrivateMessageToMember
                  isMobile={ isMobile }
                  onChange={ (name, value) => {
                     updatePayload({
                        [name]: value,
                     })
                  } }
                  value={ payload.private_message_to_member }
                  areaName='private_message_to_member'
                  title='Private message to member'
                  errorMessage={ errorsData?.private_message_to_member?.[0] }
                  clearErrorState={ () => setErrorState({ private_message_to_member: null }) }
                  modalContentWrapperId={ MODAL_CONTENT_WRAPPER_ID }
               />
               {/* Notes */}
               <SwitchMembershipNotes
                  isMobile={ isMobile }
                  title='Add a private note'
                  onChange={ (value) => {
                     updatePayload({
                        private_note: value,
                     })
                  } }
                  initialValue={ payload.private_note }
                  modalContentWrapperId={ MODAL_CONTENT_WRAPPER_ID }
               />


               {/* Duration */}
               <SwitchMembershipDuration
                  duration={ payload.duration }
                  updatePayload={ updatePayload }
                  isMobile={ isMobile }
                  authUser={ authUser }
                  errorsData={ errorsData }
                  setErrorState={ setErrorState }
                  modalContentWrapperId={ MODAL_CONTENT_WRAPPER_ID }
                  disableDuration={ payload.duration.disableDuration }
               />
            </div>
         </CreateEditModal>
         {
            confirmModal.isOpen && !confirmModalTypesOpenOnContainer.includes(confirmModal.type) && (
               <SwitchMembershipConfirmModals
                  isMobile={ isMobile }
                  type={ confirmModal.type }
                  switchingFromMembershipNamesList={ switchingFromPricingsData.map(it => it.pricing_name) }
                  newMembershipName={ payload.selectedMembershipToSwitch?.name || '' }
                  membersData={ membersData }
                  onCloseModal={ () => openCloseConfirmModal(null) }
                  onConfirm={ handleConfirmModalAction }
                  isLoading={ isFetchingSwitchMembership }
                  switchedMembersCount={ confirmModal.additionalInfo?.switchedMembersCount }
                  expiredOrIncomletedMembers={ expiredAndIncompletedMembers.expred?.length || expiredAndIncompletedMembers.incomplete?.length }
               />
            )
         }
      </>
   )
}

SwitchMembershipModal.propTypes = {
   isMobile: PropTypes.bool,
   membersData: PropTypes.array,
   onCloseModal: PropTypes.func,
   isFetchingSwitchMembership: PropTypes.bool,
   confirmModal: PropTypes.object,
   openCloseConfirmModal: PropTypes.func,
   confirmModalTypesOpenOnContainer: PropTypes.array,
   checkIfCanSwitchMembership: PropTypes.func,
   authUser: PropTypes.object,
   checkIsSubscribedToNewMembershipAndSwitch: PropTypes.func,
   switchToNewMembership: PropTypes.func,
   updateSwitchMembershipMembersData: PropTypes.func,
   isDemoMode: PropTypes.bool,
}

export default SwitchMembershipModal;
